import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getSafeTimezone } from './timezoneUtils';

// Extend dayjs with UTC and timezone plugins for proper timezone handling.
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Fixed-charge-day utilities — SINGLE SOURCE OF TRUTH.
 *
 * These were originally defined in donation-elements' scheduleConfigUtils.js and
 * moved here so BOTH the donor runtime (donation-elements) AND the dashboard
 * config validator can use the exact same anchor math without duplicating it
 * (the dashboard cannot import from donation-elements — package boundary). The
 * resolver behavior is intentionally byte-identical to the original; if you
 * change it, update the resolver tests in donation-elements.
 */

/**
 * Get the current hour (0-23) in the user's browser timezone.
 * Uses Intl.DateTimeFormat for timezone-aware hour extraction.
 *
 * @returns {number} Current hour (0-23)
 */
export const getCurrentHourInTimezone = () => {
  try {
    const tz = getSafeTimezone();
    const now = new Date();

    // Validate the date object before formatting
    if (!now || isNaN(now.getTime())) {
      return new Date().getHours();
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      hour12: false,
    });

    const formattedHour = formatter.format(now);
    const parsedHour = parseInt(formattedHour, 10);

    // Validate the parsed hour
    if (isNaN(parsedHour)) {
      return new Date().getHours();
    }

    // ECMA-402 allows Intl.DateTimeFormat to return "24" for midnight
    // Apply modulo to normalize to 0-23 range
    return parsedHour % 24;
  } catch (error) {
    console.error('[getCurrentHourInTimezone] Error formatting date:', error);
    return new Date().getHours();
  }
};

/**
 * Check if a selected time is stale (in the past) when start date is today.
 * Only returns true when the start date is today AND the selected hour is at or
 * before the current hour. 'now' and null are never stale.
 *
 * @param {string|null} timeValue - 'HH:00' format, 'now', or null
 * @param {string|null} startDateStr - 'YYYY-MM-DD' format or null
 * @returns {boolean} True if the time is in the past and needs correction
 */
export const isTimeStaleForToday = (timeValue, startDateStr) => {
  // 'now' is always valid — it dynamically means "next hour"
  if (!timeValue || timeValue === 'now' || !startDateStr) return false;

  const tz = getSafeTimezone();
  let todayInTz;
  try {
    const now = new Date();
    if (!now || isNaN(now.getTime())) {
      return false;
    }

    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);

    if (!parts || !Array.isArray(parts) || parts.length === 0) {
      return false;
    }

    const p = Object.fromEntries(parts.map(({ type, value }) => [type, value]));

    if (!p.year || !p.month || !p.day) {
      return false;
    }

    todayInTz = `${p.year}-${p.month}-${p.day}`;
  } catch (error) {
    console.error('[isTimeStaleForToday] Error formatting date:', error);
    return false;
  }

  let normalized;
  try {
    normalized = typeof startDateStr === 'string'
      ? startDateStr
      : dayjs(startDateStr).format('YYYY-MM-DD');

    if (!normalized || !/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
      return false;
    }
  } catch (error) {
    console.error('[isTimeStaleForToday] Error normalizing date:', error);
    return false;
  }

  if (normalized !== todayInTz) return false;

  try {
    if (!timeValue || typeof timeValue !== 'string') return false;

    const hourPart = timeValue.split(':')[0];
    if (!hourPart) return false;

    const selectedHour = parseInt(hourPart, 10);
    if (isNaN(selectedHour) || selectedHour < 0 || selectedHour > 23) return false;

    // Stale if selected hour is at or before the current hour
    // (need currentHour + 1 minimum for a valid future time)
    return selectedHour <= getCurrentHourInTimezone();
  } catch (error) {
    console.error('[isTimeStaleForToday] Error parsing time:', error);
    return false;
  }
};

/**
 * Resolve the next-occurrence start date for an org-fixed recurring charge day.
 *
 * This is PURE next-occurrence date resolution. It deliberately does NOT decide
 * whether the donation is charged immediately vs. scheduled, nor does it consider
 * a window END — it simply returns the first date the anchor lands on, on/after
 * the LATER of today and `windowStart`, including today when today matches.
 *
 * Frequency interpretation of `fixedChargeDay`:
 *   WEEKLY:   0-6 (day of week, Sun=0)
 *   MONTHLY:  1-31 (31 = "last day"; see seeding rule in donation-elements docs)
 *   ANNUALLY: "MM-DD"
 *   DAILY:    not applicable (returns null)
 *
 * @param {number|string|null} fixedChargeDay - The anchor (see interpretation above)
 * @param {string} frequency - Backend frequency ('WEEKLY' | 'MONTHLY' | 'ANNUALLY' | 'DAILY')
 * @param {string|null} [scheduledTime] - 'HH:mm' or 'now'; used only to skip a stale today
 * @param {string|Date|dayjs.Dayjs|null} [windowStart] - The configured schedule window start
 * @returns {string|null} Next-occurrence date as 'YYYY-MM-DD', or null when not applicable
 */
export const resolveFixedChargeDayStartDate = (
  fixedChargeDay,
  frequency,
  scheduledTime = null,
  windowStart = null,
) => {
  if (fixedChargeDay === null || fixedChargeDay === undefined || fixedChargeDay === '') {
    return null;
  }

  const tz = getSafeTimezone();
  const today = dayjs().tz(tz).startOf('day');

  // Anchor floor = the LATER of today and the configured window start.
  let anchorFloor = today;
  if (windowStart) {
    const ws = dayjs(windowStart).startOf('day');
    if (ws.isValid() && ws.isAfter(today, 'day')) {
      anchorFloor = ws;
    }
  }

  const skipToday = (candidate) =>
    candidate.isSame(today, 'day') && isTimeStaleForToday(scheduledTime, candidate.format('YYYY-MM-DD'));

  if (frequency === 'WEEKLY') {
    const target = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(target) || target < 0 || target > 6) return null;

    let candidate = anchorFloor;
    // Advance up to 7 days to find the next matching weekday (floor day included).
    for (let i = 0; i < 8; i += 1) {
      if (candidate.day() === target && !skipToday(candidate)) {
        return candidate.format('YYYY-MM-DD');
      }
      candidate = candidate.add(1, 'day');
    }
    return null;
  }

  if (frequency === 'MONTHLY') {
    const target = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(target) || target < 1 || target > 31) return null;

    // Walk forward month-by-month to the first month that HAS `target` as a real
    // day-of-month (matters for 29/30/31). Start from the anchor-floor month.
    let monthCursor = anchorFloor.startOf('month');
    for (let i = 0; i < 14; i += 1) {
      const daysInMonth = monthCursor.daysInMonth();
      if (target <= daysInMonth) {
        const candidate = monthCursor.date(target);
        if (!candidate.isBefore(anchorFloor, 'day') && !skipToday(candidate)) {
          return candidate.format('YYYY-MM-DD');
        }
      }
      monthCursor = monthCursor.add(1, 'month');
    }
    return null;
  }

  if (frequency === 'ANNUALLY') {
    // Expect "MM-DD".
    const match = String(fixedChargeDay).match(/^(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    const month = parseInt(match[1], 10); // 1-12
    const rawDay = parseInt(match[2], 10); // 1-31
    if (month < 1 || month > 12 || rawDay < 1 || rawDay > 31) return null;

    const maxDayEver = month === 2 ? 29 : ([4, 6, 9, 11].includes(month) ? 30 : 31);
    const day = Math.min(rawDay, maxDayEver);

    let yearCursor = anchorFloor.year();
    for (let i = 0; i < 12; i += 1) {
      const monthStart = dayjs.tz(`${yearCursor}-${String(month).padStart(2, '0')}-01`, tz).startOf('day');
      if (day <= monthStart.daysInMonth()) {
        const candidate = monthStart.date(day);
        if (!candidate.isBefore(anchorFloor, 'day') && !skipToday(candidate)) {
          return candidate.format('YYYY-MM-DD');
        }
      }
      yearCursor += 1;
    }
    return null;
  }

  // DAILY (or unknown) — no day-of-frequency anchor; time-only handled elsewhere.
  return null;
};

/**
 * Validate that an org-fixed recurring charge day is (a) valid for its frequency
 * and (b) actually able to fire within the configured start/end window.
 *
 * The donor's first charge lands on the first anchor occurrence on/after the
 * window start (or today if there is no window start). If that first occurrence
 * is AFTER the window end, the donation can NEVER be charged — that's a config
 * error the dashboard must block.
 *
 * @param {Object} params
 * @param {number|string|null} params.fixedChargeDay
 * @param {string} params.frequency - 'WEEKLY' | 'MONTHLY' | 'ANNUALLY' | 'DAILY'
 * @param {string|Date|dayjs.Dayjs|null} [params.windowStart] - 'YYYY-MM-DD' or null (= today)
 * @param {string|Date|dayjs.Dayjs|null} [params.windowEnd] - 'YYYY-MM-DD' or null (= no end)
 * @param {string|null} [params.scheduledTime] - 'HH:mm' or 'now'
 * @returns {'fixed_day_invalid_for_frequency'|'fixed_day_outside_window'|null}
 */
export const validateFixedChargeDayWindow = ({
  fixedChargeDay,
  frequency,
  windowStart = null,
  windowEnd = null,
  scheduledTime = null,
}) => {
  // No fixed charge day configured → nothing to validate here.
  if (fixedChargeDay === null || fixedChargeDay === undefined || fixedChargeDay === '') {
    return null;
  }

  // DAILY has no day-of-frequency anchor; a fixedChargeDay is meaningless for it.
  if (frequency === 'DAILY') return null;

  // (case 4) Range/shape check per frequency — mirrors the resolver's own guards.
  if (frequency === 'WEEKLY') {
    const target = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(target) || target < 0 || target > 6) return 'fixed_day_invalid_for_frequency';
  } else if (frequency === 'MONTHLY') {
    const target = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(target) || target < 1 || target > 31) return 'fixed_day_invalid_for_frequency';
  } else if (frequency === 'ANNUALLY') {
    const match = String(fixedChargeDay).match(/^(\d{1,2})-(\d{1,2})$/);
    if (!match) return 'fixed_day_invalid_for_frequency';
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return 'fixed_day_invalid_for_frequency';
  } else {
    // Unknown frequency — don't block.
    return null;
  }

  // No end date → the window is open-ended; the anchor will always eventually fire.
  if (!windowEnd) return null;

  // (cases 1, 2, 3) Resolve the first anchor occurrence on/after the window start
  // (the resolver floors at max(today, windowStart)), then require it on/before end.
  const firstAnchorStr = resolveFixedChargeDayStartDate(
    fixedChargeDay,
    frequency,
    scheduledTime,
    windowStart,
  );

  // If we couldn't resolve an anchor at all (shouldn't happen after the shape
  // checks above), don't block — let the resolver/runtime handle it.
  if (!firstAnchorStr) return null;

  const firstAnchor = dayjs(firstAnchorStr).startOf('day');
  const end = dayjs(windowEnd).startOf('day');
  if (!end.isValid()) return null;

  // The first charge falls after the window closes → never charges.
  if (firstAnchor.isAfter(end, 'day')) {
    return 'fixed_day_outside_window';
  }

  return null;
};
